import { useState, type FormEvent, type ReactNode } from 'react';
import { Loader2, SearchCheck } from 'lucide-react';
import { Button, Card, ProgressBar, StatusPill } from '@/components/ui';
import { useDomainCheck } from '@/hooks/useSeoMetrics';
import { DOMAIN_VERDICT, domainNoteText } from '@/utils/seo';

/** `2012–2026`, or one year alone. */
const yearSpan = (years: readonly number[]) => (years[0] === years[years.length - 1] ? String(years[0]) : `${years[0]}–${years[years.length - 1]}`);
const number = (value: number | null | undefined) => (value === null || value === undefined ? '—' : value.toLocaleString('ru-RU'));

/**
 * Names looked at before they are bought (ADR-0050): their years in the Web
 * Archive, their authority and links, and what of it is worth a second look.
 * Each name is asked on its own, three at a time, so the table fills in row
 * by row and the bar counts them.
 */
export function DomainCheckCard(): ReactNode {
  const [text, setText] = useState('');
  const check = useDomainCheck();
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (text.trim() && !check.running) check.run(text);
  };
  const total = check.domains.length;

  return (
    <Card title="Проверка доменов" icon={SearchCheck}>
      <form className="stack" onSubmit={submit}>
        <textarea
          className="input mono"
          rows={3}
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder={'example.com\nnews-example.org'}
          aria-label="Домены для проверки"
          spellCheck={false}
        />
        <div className="row-tight">
          <Button type="submit" size="sm" icon={SearchCheck} busy={check.running} disabled={!text.trim()}>
            Проверить
          </Button>
          <span className="muted">До 20 доменов: история в Web Archive, DR, ссылки и спам</span>
        </div>
        {check.invalid.length > 0 ? <p className="muted">Не похожи на домены: {check.invalid.join(', ')}</p> : null}
        {total > 0 ? (
          <div className="stack stack-tight">
            <div className="row-between text-sm">
              <span>{check.running ? 'Проверяем' : 'Проверено'}</span>
              <span className="numeric">
                {check.done} из {total}
              </span>
            </div>
            <ProgressBar value={check.done} max={total} label="Проверено доменов" tone={check.running ? 'accent' : 'healthy'} />
          </div>
        ) : null}
      </form>

      {total > 0 ? (
        <div className="table-scroll domain-check">
          <table>
            <thead>
              <tr>
                <th scope="col">Домен</th>
                <th scope="col">Вывод</th>
                <th scope="col">Web Archive</th>
                <th scope="col" className="numeric">DR</th>
                <th scope="col" className="numeric">PageRank</th>
                <th scope="col" className="numeric">Доменов ссылается</th>
                <th scope="col" className="numeric">Спам</th>
                <th scope="col">На что посмотреть</th>
              </tr>
            </thead>
            <tbody>
              {check.domains.map((domain) => {
                const result = check.results[domain];
                const error = check.errors[domain];
                if (!result) {
                  return (
                    <tr key={domain}>
                      <td className="mono">{domain}</td>
                      <td colSpan={7}>
                        {error ? (
                          <span className="tone-down">{error}</span>
                        ) : (
                          <span className="row-tight muted">
                            <Loader2 size={14} className="spin" aria-hidden="true" />
                            проверяется…
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                }
                const wayback = result.wayback?.data;
                const dfs = result.dataforseo?.data;
                const opr = result.openpagerank?.data;
                const ahrefs = result.ahrefs?.data;
                return (
                  <tr key={domain}>
                    <td className="mono">{domain}</td>
                    <td>
                      <StatusPill map={DOMAIN_VERDICT} status={result.verdict ?? 'none'} />
                    </td>
                    <td className="nowrap">
                      {wayback ? (wayback.years.length > 0 ? `${yearSpan(wayback.years)} · ${wayback.years.length} г.` : 'нет снимков') : result.wayback?.error ? <span className="tone-down">ошибка</span> : '—'}
                    </td>
                    <td className="numeric">{number(ahrefs?.domainRating)}</td>
                    <td className="numeric">{opr?.pageRank !== undefined && opr.pageRank !== null ? opr.pageRank.toLocaleString('ru-RU', { maximumFractionDigits: 1 }) : '—'}</td>
                    <td className="numeric">{number(dfs?.referringDomains ?? opr?.referringDomains)}</td>
                    <td className="numeric">{number(dfs?.spamScore ?? dfs?.backlinksSpamScore)}</td>
                    <td className="text-sm">{result.notes.length > 0 ? result.notes.map(domainNoteText).join('; ') : <span className="faint">—</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </Card>
  );
}
